// @seventide/types — platform area: executor pack & archive (FR-10.1, FR-10.2). Re-exported by ../platform.ts.
import { z } from 'zod';
import { Id, IsoDate, IsoDateTime } from '../common';
import { Stage } from '../enums';
import { BILLING_PLAN_INFO } from './billing';

/** FR-10.2: keep the archive this many years after the date of death unless the owner chooses otherwise. */
export const ARCHIVE_RETENTION_DEFAULT_YEARS = 7;
export const ARCHIVE_RETENTION_MIN_YEARS = 1;
export const ARCHIVE_RETENTION_MAX_YEARS = 25;
/** The owner is asked to review the retention period this many days before it ends. */
export const ARCHIVE_REVIEW_LEAD_DAYS = 90;
/** Archive plan (FR-10.2) — single source: the billing area's plan table. */
export const EXECUTOR_ARCHIVE_PLAN = BILLING_PLAN_INFO.archive;

/** `exports.type` values written by the executor area (Home reads `executor%`). */
export const EXECUTOR_PACK_TYPE = 'executor_pack';
export const EXECUTOR_ORIGINALS_TYPE = 'executor_originals';

export const EXECUTOR_PACK_SECTIONS = [
  'gifts',
  'income_expenditure',
  'assets',
  'care_fees',
  'attorney_accounts',
  'decisions',
  'documents',
] as const;
export const ExecutorPackSection = z.enum(EXECUTOR_PACK_SECTIONS);
export type ExecutorPackSection = z.infer<typeof ExecutorPackSection>;

export const EXECUTOR_PACK_SECTION_LABELS: Record<ExecutorPackSection, string> = {
  gifts: 'Gifts schedule (IHT403 layout)',
  income_expenditure: 'Income and spending by tax year',
  assets: 'Assets and debts with last balances',
  care_fees: 'Care fees, refunds and claims',
  attorney_accounts: 'Attorney accounts to the date of death',
  decisions: 'Decision log',
  documents: 'Document index',
};

export const EXECUTOR_PACK_SECTION_HINTS: Record<ExecutorPackSection, string> = {
  gifts: 'Every gift with the recipient, value and the exemptions recorded, laid out like HMRC form IHT403.',
  income_expenditure: 'Yearly income, spending and surplus, used to support gifts made from income.',
  assets: 'Each account with its last recorded balance and the statements linked to it.',
  care_fees: 'Care contracts, invoices, nursing care (FNC) credits and any NHS or council decisions recorded.',
  attorney_accounts: 'Payments made by attorneys and their expenses, up to the date of death.',
  decisions: 'Decisions recorded by attorneys, with who signed them.',
  documents: 'A list of every document in the vault, by category and date.',
};

export const RecordDateOfDeathRequest = z.object({
  dateOfDeath: IsoDate,
  /** The person ticked "I understand the workspace becomes an archive". */
  confirm: z.literal(true, { message: 'Please confirm you understand what happens next' }),
});
export type RecordDateOfDeathRequest = z.infer<typeof RecordDateOfDeathRequest>;

export const CorrectDateOfDeathRequest = z.object({ dateOfDeath: IsoDate });
export type CorrectDateOfDeathRequest = z.infer<typeof CorrectDateOfDeathRequest>;

export const RemoveDateOfDeathRequest = z.object({
  confirm: z.literal(true, { message: 'Please confirm you want to remove the date of death' }),
});
export type RemoveDateOfDeathRequest = z.infer<typeof RemoveDateOfDeathRequest>;

export const ArchiveRetentionRequest = z.object({
  years: z
    .number({ message: 'Choose how many years to keep the archive' })
    .int('Choose a whole number of years')
    .min(ARCHIVE_RETENTION_MIN_YEARS, `Choose at least ${ARCHIVE_RETENTION_MIN_YEARS} year`)
    .max(ARCHIVE_RETENTION_MAX_YEARS, `Choose ${ARCHIVE_RETENTION_MAX_YEARS} years or fewer`),
});
export type ArchiveRetentionRequest = z.infer<typeof ArchiveRetentionRequest>;

export const BuildExecutorPackRequest = z.object({
  sections: z.array(ExecutorPackSection).min(1, 'Choose at least one section').max(EXECUTOR_PACK_SECTIONS.length),
});
export type BuildExecutorPackRequest = z.infer<typeof BuildExecutorPackRequest>;

export const ExecutorPackDto = z.object({
  id: Id,
  type: z.string(),
  title: z.string(),
  format: z.enum(['pdf', 'csv', 'zip', 'json']),
  status: z.enum(['pending', 'rendering', 'ready', 'failed']),
  createdAt: IsoDateTime,
  sizeBytes: z.number().int().nullable(),
  filename: z.string().nullable(),
  downloadUrl: z.string().nullable(),
});
export type ExecutorPackDto = z.infer<typeof ExecutorPackDto>;

export const ArchiveRetentionDto = z.object({
  years: z.number().int(),
  /** ISO date the archive is kept until (date of death + years). */
  until: IsoDate,
  /** When the owner is asked to review (until − 90 days). */
  reviewOn: IsoDate,
  expired: z.boolean(),
});
export type ArchiveRetentionDto = z.infer<typeof ArchiveRetentionDto>;

/** GET /api/w/:id/platform/executor */
export const ExecutorOverview = z.object({
  stage: Stage,
  archive: z.boolean(),
  dateOfDeath: IsoDate.nullable(),
  recordedAt: IsoDateTime.nullable(),
  recordedByName: z.string().nullable(),
  correctedAt: IsoDateTime.nullable(),
  /** Earliest date allowed for the date of death (the Principal's date of birth, when known). */
  earliestDate: IsoDate.nullable(),
  /** Owner or (registered) attorney, before a date of death is recorded. */
  canRecord: z.boolean(),
  /** Owner only, after a date of death is recorded. */
  canCorrect: z.boolean(),
  /** Owner, attorney or Principal, after a date of death is recorded and not read-only. */
  canBuildPack: z.boolean(),
  canSetRetention: z.boolean(),
  retention: ArchiveRetentionDto.nullable(),
  defaultRetentionYears: z.number().int(),
  archivePlan: z.object({ name: z.string(), pricePence: z.number().int(), interval: z.enum(['month', 'year']) }),
  sections: z.array(z.object({ key: ExecutorPackSection, label: z.string(), hint: z.string(), recordCount: z.number().int() })),
  packs: z.array(ExecutorPackDto),
});
export type ExecutorOverview = z.infer<typeof ExecutorOverview>;

/** UK calendar arithmetic used by the API and web: add whole years to an ISO date (29 Feb → 28 Feb). */
export function addYearsIso(iso: string, years: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const ty = y + years;
  const last = new Date(Date.UTC(ty, m, 0)).getUTCDate();
  return `${String(ty).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`;
}

/** Add (or subtract) days to an ISO date. */
export function addDaysToIso(iso: string, days: number): string {
  const t = Date.parse(`${iso}T00:00:00Z`) + days * 86_400_000;
  return new Date(t).toISOString().slice(0, 10);
}
